// src/pages/dashboard/branch/analytics/ClientsAnalyticsTab.tsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiUsers } from "react-icons/fi";
import { FaSearch, FaWhatsapp } from "react-icons/fa";
import Card from "../../../../components/ui/Card";
import { getClients, ClientInfo } from "../../../../utils/supabaseClient";
import PaginationWithLimitSelector from "../../../../components/ui/PaginationWithLimitSelector";

interface ClientsAnalyticsTabProps {
  branchId: string;
}

const ClientsAnalyticsTab: React.FC<ClientsAnalyticsTabProps> = ({ branchId }) => {
  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [loadingClients, setLoadingClients] = useState<boolean>(false);
  const [clientSearchTerm, setClientSearchTerm] = useState<string>("");
  const [clientCurrentPage, setClientCurrentPage] = useState<number>(1);
  const [clientItemsPerPage, setClientItemsPerPage] = useState<number>(10);
  const [totalClients, setTotalClients] = useState<number>(0);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchClients = async () => {
      if (branchId) {
        setLoadingClients(true);
        const result = await getClients({
          branch_id: branchId,
          offset: (clientCurrentPage - 1) * clientItemsPerPage,
          limit: clientItemsPerPage,
          first_name: clientSearchTerm || undefined,
        });
        console.log('Clients fetch result:', result);
        if (result.success) {
          console.log('Setting clients:', result.data);
          setClients(result.data);
          setTotalClients(result.total_count || 0);
        } else {
          console.error('Failed to fetch clients:', result.message);
        }
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, [branchId, clientCurrentPage, clientItemsPerPage, clientSearchTerm]);

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('analytics.searchClients', 'Search by name...')}
              value={clientSearchTerm}
              onChange={(e) => setClientSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Clients Table */}
        {loadingClients ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12">
            <FiUsers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">{t('analytics.noClients', 'No clients found')}</p>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600"
                >
                  {/* Header: Name and Contact */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {client.first_name} {client.last_name}
                      </h3>
                      {client.phone_number && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          {client.phone_number}
                        </p>
                      )}
                    </div>
                    {client.phone_number && (
                      <a
                        href={`https://wa.me/${client.phone_number.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center p-2 rounded-full bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                        title={t("analytics.contactViaWhatsApp")}
                      >
                        <FaWhatsapp className="w-5 h-5 text-green-500" />
                      </a>
                    )}
                  </div>

                  {/* Zones */}
                  {client.zones && client.zones.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('analytics.clientZones', 'Zones')}</p>
                      <div className="flex flex-wrap gap-1">
                        {client.zones.map((zone, idx) => (
                          <span key={idx} className="text-xs inline-flex items-center px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-500 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                            {i18n.language === 'ar' ? (zone.ar_zone_name || zone.zone_name) : zone.zone_name}
                            <span className="ms-1 text-gray-400 dark:text-gray-500 text-[10px]">({zone.order_count})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded-lg bg-white dark:bg-gray-800 text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('analytics.clientOrders', 'Orders')}</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{client.number_of_orders}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-white dark:bg-gray-800 text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('analytics.lastOrder', 'Last Order')}</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {client.last_order_at ? new Date(client.last_order_at).toLocaleDateString() : '-'}
                      </p>
                    </div>
                    <div className="p-2 rounded-lg bg-white dark:bg-gray-800 text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('analytics.clientTotalSpent', 'Total Spent')}</p>
                      <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">${client.total_order_amount.toFixed(2)}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-white dark:bg-gray-800 text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('analytics.clientAvgOrder', 'Avg Order')}</p>
                      <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">${client.average_order_amount.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('analytics.clientName', 'Name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('analytics.clientPhone', 'Phone')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('analytics.clientZones', 'Zones')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('analytics.lastOrder', 'Last Order')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('analytics.clientOrders', 'Orders')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('analytics.clientTotalSpent', 'Total Spent')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('analytics.clientAvgOrder', 'Avg Order')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('analytics.contact', 'Contact')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {client.first_name} {client.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {client.phone_number || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {client.zones && client.zones.length > 0 ? (
                            client.zones.map((zone, idx) => (
                              <span key={idx} className="text-xs inline-flex items-center px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                {i18n.language === 'ar' ? (zone.ar_zone_name || zone.zone_name) : zone.zone_name}
                                <span className="ms-1 text-gray-400 dark:text-gray-500 text-[10px]">({zone.order_count})</span>
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {client.last_order_at ? new Date(client.last_order_at).toLocaleDateString() : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {client.number_of_orders}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          ${client.total_order_amount.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          ${client.average_order_amount.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {client.phone_number ? (
                          <a
                            href={`https://wa.me/${client.phone_number.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center p-2 rounded-full hover:bg-green-100 dark:hover:bg-green-900 transition-colors"
                            title={t("analytics.contactViaWhatsApp")}
                          >
                            <FaWhatsapp className="w-5 h-5 text-green-500 hover:text-green-600" />
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination for Clients */}
        {totalClients > 0 && (
          <div className="mt-6">
            <PaginationWithLimitSelector
              currentPage={clientCurrentPage}
              totalPages={Math.ceil(totalClients / clientItemsPerPage)}
              totalItems={totalClients}
              itemsPerPage={clientItemsPerPage}
              onPageChange={setClientCurrentPage}
              onLimitChange={(limit) => {
                setClientItemsPerPage(limit);
                setClientCurrentPage(1);
              }}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClientsAnalyticsTab;
